import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { DataGrid } from "@mui/x-data-grid";

import PreBaseline from "../model/PreBaseline";
import { Box, Tooltip } from "@mui/material";
import { Snackbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import { CircularProgress } from "@mui/material";
import { projectPrebaselines } from "../operations/ProjectOperations";
import DialogEditBaselineData from "../dialogs/DialogEditBaselineData";

const fromatDateString = (dateString) => {
  let date = new Date(dateString);
  return (
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "/" +
    ("0" + date.getDate()).slice(-2) +
    "/" +
    date.getFullYear()
  );
};

const generateTableRow = (task, formattedPreBaseline, readOnly, setIsTaskPackage) => {
  let formattedPreBaselineArr =
    formattedPreBaseline.rootTaskPackage.depthFirstTraversal();
  let formattedPreBaselineInfo = formattedPreBaselineArr.filter(
    (taskAndPackages) => taskAndPackages.code === task.code
  );
  setIsTaskPackage(readOnly);

  if (formattedPreBaselineInfo.length === 0) {
    return null;
  }

  return {
    baselineNumber: formattedPreBaseline.baselineNumber,
    preBaselinePeriod: formattedPreBaseline.preBaselinePeriod,
    dateEffective: fromatDateString(formattedPreBaseline.dateEffective),
    plannedStartDate: fromatDateString(
      formattedPreBaselineInfo[0].plannedStartDate
    ),
    plannedCost: `$${parseFloat(
      formattedPreBaselineInfo[0].plannedCost
    ).toLocaleString()}`,
    plannedDuration: formattedPreBaselineInfo[0].plannedDuration,
    plannedEndDate: fromatDateString(
      formattedPreBaselineInfo[0].plannedEndDate
    ),
    baselineDefination: formattedPreBaseline.baselineDescription,
  };
};

const TaskBaselines = ({ task, readOnly }) => {
  const [data, setData] = useState([]);
  const [loadingPrebaseline, setloadingPrebaseline] = useState(false);
  const [isEditBaselineOpen, setEditBaselineOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const [isTaskPackage, setIsTaskPackage] = useState(true);

  useEffect(() => {
    if (
      task.project.preBaselines === undefined ||
      task.project.preBaselines === null
    ) {
      task.project.preBaselines = [];
      projectPrebaselines(
        task.project,
        setloadingPrebaseline,
        (returedProjPrebaseline) => {
          returedProjPrebaseline.forEach((preBaseline) => {
            let formatedPreBaseline = PreBaseline.fromJS(preBaseline);
            task.project.preBaselines.push(formatedPreBaseline);
          });

          setData(
            returedProjPrebaseline
              .filter((x) => x.isArchive === false)
              .map((preBaseline) => {
                let formatedPreBaseline = PreBaseline.fromJS(preBaseline);

                return generateTableRow(task, formatedPreBaseline, readOnly, setIsTaskPackage);
              })
              .filter(element => element !== null)
          );
        }
      );
    } else {
      setData(
        task.project.preBaselines
          .filter((x) => x.isArchive === false)
          .map((formatedPreBaseline) => {
            return generateTableRow(task, formatedPreBaseline, readOnly, setIsTaskPackage);
          })
          .filter(element => element !== null)
      );
    }
  }, [task, readOnly]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setloadingPrebaseline(false);
  };

  const handleEditBaselineOpen = (params) => {
    setSelectedRowId(params.row.id);
    setEditBaselineOpen(true);
  };

  const handleEditBaselineClose = () => {
    setEditBaselineOpen(false);
  };

  const columns = [
    {
      field: "id",
      headerName: "No.",
      width: 99,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "period",
      headerName: "Period",
      width: 118,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "dateEffective",
      headerName: "Date Effective",
      width: 170,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "plannedStartDate",
      headerName: "Planned Start Date",
      width: 197,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "plannedCost",
      headerName: "Planned Cost",
      width: 165,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "plannedDuration",
      headerName: "Planned Duration",
      width: 190,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "plannedEndDate",
      headerName: "Planned End Date",
      width: 194,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
    },
    {
      field: "baselineDefination",
      headerName: "Note",
      width: 167,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditable";
        }
      },
      renderCell: (params) => (
        <Tooltip title={params.value ?? " "}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  const rows = data.map((row) => ({
    id: row.baselineNumber,
    period: row.preBaselinePeriod,
    dateEffective: row.dateEffective,
    plannedStartDate: row.plannedStartDate,
    plannedCost: row.plannedCost,
    plannedDuration: row.plannedDuration,
    plannedEndDate: row.plannedEndDate,
    baselineDefination: row.baselineDefination,
  }));

  return (
    <div>
      <Box
        sx={{
          height: 900,
          width: 1300,
          "& .editable": {
            backgroundColor: "rgba(225, 255, 255, 0)",
            color: "#000000",
            cursor: "pointer"
          },
          "& .uneditable": {
            backgroundColor: "rgba(225, 255, 255, 0)",
            color: "#808080",
            cursor: "pointer"
          },
        }}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[15]}
          pageSize={15}
          pagination
          onRowClick={handleEditBaselineOpen}
        />
        <Snackbar
          title="Load PreBaseline Message"
          open={loadingPrebaseline}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="info"
            sx={{ width: "100%" }}
          >
            <div>
              <CircularProgress color="inherit" />
              Please wait while the baseline snapshot is being generated, this may
              take several minutes
            </div>
          </Alert>
        </Snackbar>
        <DialogEditBaselineData
          open={isEditBaselineOpen}
          selectedTask={task}
          rowId={selectedRowId}
          readOnly={readOnly}
          onClose={handleEditBaselineClose}
        />
      </Box>
    </div>
  );
};


export default observer(TaskBaselines);
